import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import PermissionGate from "@/components/PermissionGate";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, Plus, Pencil } from "lucide-react";

const EMPTY = { branch_code: "", name: "", type: "Physical", city: "", state: "", phone: "", address: "", is_active: true };

export default function BranchesTab() {
  const { reloadBranches } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/branches");
    setRows(data || []);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (b) => { setForm({ ...EMPTY, ...b }); setEditId(b.id); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editId) await api.put(`/branches/${editId}`, form);
      else await api.post("/branches", form);
      toast.success(editId ? "Branch updated" : "Branch created");
      setOpen(false);
      await load();
      await reloadBranches();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="branches-tab">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="font-display text-lg font-semibold text-secondary">Branches</h3>
          <p className="text-sm text-muted-foreground">Manage institute locations.</p>
        </div>
        <PermissionGate permission="branch.manage">
          <Button onClick={openNew} data-testid="add-branch-button"><Plus className="mr-1.5 h-4 w-4" /> Add branch</Button>
        </PermissionGate>
      </div>

      <div className="rounded-xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead>
              <TableHead>City</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((b) => (
              <TableRow key={b.id} data-testid={`branch-row-${b.branch_code}`}>
                <TableCell className="font-medium">{b.branch_code}</TableCell>
                <TableCell className="flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" />{b.name}</TableCell>
                <TableCell>{b.type}</TableCell>
                <TableCell>{b.city || "—"}</TableCell>
                <TableCell>
                  <Badge className={b.is_active === false ? "border-0 bg-slate-100 text-slate-600" : "border-0 bg-accent/10 text-accent"}>
                    {b.is_active === false ? "Inactive" : "Active"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <PermissionGate permission="branch.manage">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(b)} data-testid={`edit-branch-${b.branch_code}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </PermissionGate>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="branch-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit branch" : "Add branch"}</DialogTitle>
            <DialogDescription>Set the branch code, name and location details.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Branch code</Label><Input value={form.branch_code} onChange={set("branch_code")} data-testid="branch-code-input" /></div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="branch-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Physical">Physical</SelectItem><SelectItem value="Online">Online</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={set("name")} data-testid="branch-name-input" /></div>
            <div><Label>City</Label><Input value={form.city} onChange={set("city")} /></div>
            <div><Label>State</Label><Input value={form.state} onChange={set("state")} /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} /></div>
            <div><Label>Address</Label><Input value={form.address} onChange={set("address")} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-branch-button">{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
